import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, Download, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, download, errMsg, fmtDT, todayIST } from "@/lib/api";
import { PageHeader, Empty, tableWrap, th, td } from "@/components/common";
import { Badge } from "@/components/ui/badge";

const TONE = { accepted: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30", delivered: "bg-emerald-600/20 text-emerald-800 border-emerald-600/40",
  failed: "bg-red-500/15 text-red-700 border-red-500/30", retrying: "bg-amber-500/15 text-amber-700 border-amber-500/30" };

export default function EmailReports() {
  const [items, setItems] = useState([]);
  const [day, setDay] = useState(todayIST());
  const load = () => api.get("/email-reports").then((r) => setItems(r.data));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);
  const sendNow = async () => {
    if (!window.confirm(`Generate the ${day} report now and email it to roxycables@gmail.com?`)) return;
    try { await api.post(`/email-reports/send-now?day=${day}`); toast.success("Report queued — status updates below"); setTimeout(load, 1500); } catch (e) { toast.error(errMsg(e)); }
  };
  const resend = async (r) => {
    if (!window.confirm(`Resend ${r.filename}?`)) return;
    try { await api.post(`/email-reports/${r.id}/resend`); toast.success("Resend queued"); setTimeout(load, 1500); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Daily Email Reports" subtitle="Automatic Excel report to roxycables@gmail.com every day at 23:59 IST (Asia/Kolkata), including Sundays and holidays. 'Accepted' means the email provider accepted the message, not confirmed inbox delivery.">
        <Input type="date" value={day} max={todayIST()} onChange={(e) => setDay(e.target.value)} className="w-40" data-testid="email-report-day-input" />
        <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={sendNow} data-testid="email-report-send-now-btn"><Send className="h-4 w-4 mr-1" />Generate &amp; send now</Button>
        <Button variant="outline" size="icon" onClick={load} data-testid="email-report-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
      </PageHeader>
      <div className={tableWrap}>
        <table className="w-full" data-testid="email-reports-table">
          <thead><tr>{["Report Date", "Version", "Trigger", "Generated", "Cutoff", "Recipient", "Excel File", "Status", "Provider Message ID", "Attempts", "Last Attempt", "Error", "Resends", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{items.map((r) => (
            <tr key={r.id} data-testid={`email-report-row-${r.report_date}-v${r.version}`}>
              <td className={`${td} font-mono`}>{r.report_date}</td><td className={td}>v{r.version}</td><td className={td}>{r.trigger}</td><td className={td}>{fmtDT(r.generated_at)}</td>
              <td className={td}>{r.cutoff}</td><td className={td}>{r.recipient}</td><td className={`${td} font-mono text-xs`}>{r.filename}</td>
              <td className={td}><Badge variant="outline" className={`capitalize ${TONE[r.status] || ""}`} data-testid={`email-report-status-${r.report_date}-v${r.version}`}>{r.status}</Badge></td>
              <td className={`${td} font-mono text-xs`}>{r.provider_message_id || "—"}</td><td className={td}>{r.attempts}</td><td className={td}>{fmtDT(r.last_attempt)}</td>
              <td className={`${td} max-w-xs truncate text-red-600 text-xs`} title={r.error || ""}>{r.error || "—"}</td>
              <td className={`${td} text-xs`}>{r.resend_history.map((h) => `${fmtDT(h.at)} by ${h.by.name}`).join("; ") || "—"}</td>
              <td className={td}><div className="flex">
                <Button size="icon" variant="ghost" title="Download" onClick={() => download(`/email-reports/${r.id}/download`, r.filename)} data-testid={`email-report-download-${r.id}`}><Download className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Resend" onClick={() => resend(r)} data-testid={`email-report-resend-${r.id}`}><RotateCcw className="h-4 w-4" /></Button>
              </div></td>
            </tr>
          ))}</tbody>
        </table>
        {!items.length && <Empty text="No reports generated yet. The first automatic report runs tonight at 23:59 IST." />}
      </div>
    </div>
  );
}
